import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { EventData, LocationType, LOCATION_TYPE_LABELS, INDUSTRY_LABELS, COMPANY_SCALE_LABELS } from '../../types/event';
import { getDeadlineDiffDays } from '../../utils/dateUtils';
import { EventApplyFormDialog } from './EventApplyFormDialog';
import { ApplySuccessDialog } from './ApplySuccessDialog';
import { EventParticipantsDialog } from './EventParticipantsDialog';
import { ApplyProfileData } from '../../hooks/useEventParticipation';

const APPLY_REQUIRED_KEYS = ['fullName', 'furigana', 'university', 'faculty', 'phone', 'birthDate', 'gender'] as const;

const DAY_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const formatEventDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}月${d.getDate()}日（${DAY_OF_WEEK[d.getDay()]}）`;
};

const formatTime = (t: string): string => t.slice(0, 5);
import { CategoryBadge } from './CategoryBadge';
import { DeadlineLabel } from './DeadlineLabel';
import { useEventDetail } from '../../hooks/useEventDetail';
import { useEventParticipation } from '../../hooks/useEventParticipation';

interface EventDetailDialogProps {
  eventId: number | null;
  userRole: string;
  open: boolean;
  onClose: () => void;
  onDelete: (event: EventData) => void;
  onParticipationChange: (eventId: number, isParticipated: boolean) => void;
  onSnackbar: (message: string, severity: 'success' | 'error' | 'warning') => void;
}

export const EventDetailDialog: React.FC<EventDetailDialogProps> = ({
  eventId,
  userRole,
  open,
  onClose,
  onDelete,
  onParticipationChange,
  onSnackbar,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { event, isLoading, error, fetch, clear } = useEventDetail();
  const { isSubmitting, apply, cancel } = useEventParticipation();
  const isAdmin         = userRole === 'admin';
  const isStudent       = userRole === 'student';
  const isCompanyEditor = ['company_admin', 'hr'].includes(userRole);
  const canEdit         = isAdmin || (isCompanyEditor && !!event?.is_own_company);

  const [applyFormOpen,      setApplyFormOpen]      = useState(false);
  const [isCheckingProfile,  setIsCheckingProfile]  = useState(false);
  const [successDialogOpen,  setSuccessDialogOpen]  = useState(false);
  const [appliedLocationType, setAppliedLocationType] = useState<LocationType>('offline');
  const [successDialogType,  setSuccessDialogType]  = useState<'apply' | 'cancel'>('apply');
  const [participantsOpen,   setParticipantsOpen]   = useState(false);

  const isDeadlinePassed = !!event?.application_deadline &&
    getDeadlineDiffDays(event.application_deadline) < 0;

  useEffect(() => {
    if (open && eventId !== null) {
      fetch(eventId);
    } else {
      clear();
    }
  }, [open, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 申込ボタン → プロフィール確認して直接申込 or フォームを開く
  const handleParticipateClick = async () => {
    if (!event) return;
    setIsCheckingProfile(true);
    try {
      const res = await axios.get<{ success: boolean; data: { user: Record<string, unknown> } }>(
        '/api/users/profile', { withCredentials: true }
      );
      const profile = res.data.data.user;
      const allFilled = APPLY_REQUIRED_KEYS.every((k) => !!profile[k]);

      if (allFilled) {
        // 全項目揃っていれば確認フォームなしで直接申込
        const profileData: ApplyProfileData = {
          fullName:   profile.fullName   as string,
          furigana:   profile.furigana   as string,
          university: profile.university as string,
          faculty:    profile.faculty    as string,
          phone:      profile.phone      as string,
          birthDate:  profile.birthDate  as string,
          gender:     profile.gender     as string,
        };
        const result = await apply(event.id, profileData);
        if (result.success) {
          onParticipationChange(event.id, true);
          setAppliedLocationType(event.location_type);
          setSuccessDialogType('apply');
          setSuccessDialogOpen(true);
        } else {
          onSnackbar(result.message, 'error');
        }
      } else {
        // 未入力項目があればフォームを開く
        setApplyFormOpen(true);
      }
    } catch {
      // プロフィール取得失敗時もフォームを開いてフォールバック
      setApplyFormOpen(true);
    } finally {
      setIsCheckingProfile(false);
    }
  };

  // フォーム確定 → 申込実行
  const handleApplySubmit = async (profileData: ApplyProfileData) => {
    if (!event) return;
    const result = await apply(event.id, profileData);
    if (result.success) {
      setApplyFormOpen(false);
      onParticipationChange(event.id, true);
      setAppliedLocationType(event.location_type);
      setSuccessDialogType('apply');
      setSuccessDialogOpen(true);
    } else {
      setApplyFormOpen(false);
      onSnackbar(result.message, 'error');
    }
  };

  const handleCancel = async () => {
    if (!event) return;
    const result = await cancel(event.id);
    if (result.success) {
      onParticipationChange(event.id, false);
      fetch(event.id);
      setAppliedLocationType(event.location_type);
      setSuccessDialogType('cancel');
      setSuccessDialogOpen(true);
    } else {
      onSnackbar(result.message, 'warning');
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    onClose();
  };

  return (
    <>
    {event && (
      <EventApplyFormDialog
        open={applyFormOpen}
        onClose={() => setApplyFormOpen(false)}
        onSubmit={handleApplySubmit}
        eventTitle={event.title}
        isSubmitting={isSubmitting}
      />
    )}
    <ApplySuccessDialog
      open={successDialogOpen}
      onClose={handleSuccessDialogClose}
      locationType={appliedLocationType}
      type={successDialogType}
    />
    {event && (
      <EventParticipantsDialog
        open={participantsOpen}
        onClose={() => setParticipantsOpen(false)}
        eventId={event.id}
        eventTitle={event.title}
      />
    )}
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700}>
            イベント詳細
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {canEdit && event && (
              <>
                <IconButton size="small" onClick={() => { onClose(); navigate(`/admin/events/${event.id}/edit`); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => onDelete(event)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {event && !isLoading && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <CategoryBadge category={event.category} />
              {(event.tags ?? []).map((tag) => (
                <Box
                  key={tag.id}
                  sx={{ display: 'inline-block', backgroundColor: '#ECEFF1', color: '#546E7A', borderRadius: '6px', px: 1, py: 0.25, fontSize: '0.72rem', fontWeight: 600 }}
                >
                  {tag.name}
                </Box>
              ))}
              {event.is_participated && (
                <Chip label="参加申込済み" size="small" color="primary" />
              )}
            </Stack>

            <Typography variant="h6" fontWeight={700}>
              {event.title}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <BusinessIcon sx={{ color: 'text.secondary', fontSize: 18, mt: '2px', flexShrink: 0 }} />
              <Box>
                <Typography variant="body2">{event.company_name}</Typography>
                {(event.industry || event.company_scale) && (
                  <Typography variant="caption" color="text.secondary">
                    {[
                      event.industry      && INDUSTRY_LABELS[event.industry],
                      event.company_scale && COMPANY_SCALE_LABELS[event.company_scale],
                    ].filter(Boolean).join('・')}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="body2">
                {formatEventDate(event.event_date)}　{formatTime(event.start_time)}〜{formatTime(event.end_time)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="body2">
                {LOCATION_TYPE_LABELS[event.location_type]}　{event.location}
              </Typography>
            </Stack>

            {event.location_type === 'online' && event.meeting_url && (
              <Stack direction="row" spacing={1} alignItems="center">
                <VideoCallIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                {/^https?:\/\//i.test(event.meeting_url) ? (
                  <Link
                    href={event.meeting_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    underline="hover"
                  >
                    会議URLを開く
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {event.meeting_url}
                  </Typography>
                )}
              </Stack>
            )}

            {event.location_type === 'online' && !event.meeting_url && isStudent && !event.is_participated && (
              <Stack direction="row" spacing={1} alignItems="center">
                <VideoCallIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  会議URLは申込完了後に確認できます
                </Typography>
              </Stack>
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              <PeopleIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="body2">
                定員 {event.capacity}名
                {(isStudent || isAdmin) && event.current_participants !== undefined &&
                  ` / 申込 ${event.current_participants}名`}
              </Typography>
            </Stack>

            <Box>
              <DeadlineLabel event={event} />
            </Box>

            {event.description && (
              <>
                <Divider />
                <Typography variant="body2" whiteSpace="pre-wrap">
                  {event.description}
                </Typography>
              </>
            )}

            {isAdmin && event.unread_notification_count !== undefined && event.unread_notification_count > 0 && (
              <Alert severity="info">
                未読通知 {event.unread_notification_count}件
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, flexWrap: 'nowrap' }}>
        <Button onClick={onClose}>閉じる</Button>
        {(isAdmin || (isCompanyEditor && !!event?.is_own_company)) && event && (
          <Button
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={() => setParticipantsOpen(true)}
          >
            参加者を確認
          </Button>
        )}
        {isStudent && event && !event.is_past && (
          event.is_participated ? (
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={14} /> : undefined}
            >
              参加をキャンセル
            </Button>
          ) : isDeadlinePassed ? (
            <Button variant="contained" disabled sx={{ bgcolor: 'action.disabledBackground' }}>
              申込締切済み
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleParticipateClick}
              disabled={isSubmitting || isCheckingProfile}
              startIcon={(isSubmitting || isCheckingProfile) ? <CircularProgress size={14} /> : undefined}
            >
              参加申込
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
    </>
  );
};
